import { OrderDetail } from '@interfaces/OrderTypes';
import { useGetCommerce } from '@pages/Commerce/hooks/commerce';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { useIntegrations } from '@pages/Integrationsv3/hooks';
import { buildKitchenTicketPayload, buildTicketPayload } from '@utils/printUtils';
import type { PrintContext, PrinterConfig, PrintJobPayload } from '@interfaces/PrintServer';
import type { IntegrationConfigurationPrinters, ProviderCode } from '@interfaces/Integration';

interface RawPrintServerConfig {
  printers: IntegrationConfigurationPrinters[];
  url?: string;
  api_key?: string;
}

export const usePrintServer = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { commerce } = useGetCommerce();
  const { getIntegration } = useIntegrations();

  const getPlickPrintServerConfig = () => {
    const integration = getIntegration('plickPrintServer') || getIntegration('plick_print_server' as ProviderCode);
    const config = integration?.configuration as unknown as RawPrintServerConfig;

    let localPrinters: IntegrationConfigurationPrinters[] = [];
    try {
      const stored = localStorage.getItem('plick_print_server_printers');
      if (stored) localPrinters = JSON.parse(stored);
    } catch {}

    const printersSource = localPrinters.length > 0 ? localPrinters : config?.printers;

    return {
      config,
      printersSource,
    };
  };

  /**
   * Obtiene las impresoras configuradas para un contexto específico
   * @param {PrintContext} context - Contexto de impresión (order, kitchen, self_service, auto_print)
   * @returns {PrinterConfig[]} Lista de impresoras configuradas
   */
  const getPrintersForContext = (context: PrintContext): PrinterConfig[] => {
    const { printersSource } = getPlickPrintServerConfig();
    if (!printersSource) return [];

    return printersSource
      .filter((printer) => {
        switch (context) {
          case 'order':
            return printer.use_for.orders;
          case 'self_service':
            return printer.use_for.self_service;
          case 'kitchen':
            return printer.use_for.kitchen;
          case 'auto_print':
            return printer.use_for.auto_print;
          default:
            return false;
        }
      })
      .map(
        (printer) =>
          ({
            name: printer.name,
            printer_id: printer.api_key,
            use_for: printer.use_for,
            url: printer.url,
          }) as PrinterConfig,
      );
  };

  /**
   * Verifica si hay impresoras configuradas para un contexto
   * @param {PrintContext} [context] - Contexto opcional
   * @returns {boolean} True si hay impresoras configuradas
   */
  const isPrintServerConfigured = (context?: PrintContext): boolean => {
    if (!context) {
      const { printersSource } = getPlickPrintServerConfig();
      return Boolean(printersSource && printersSource.length > 0);
    }
    return getPrintersForContext(context).length > 0;
  };

  /**
   * Envía el trabajo de impresión al servidor
   * @param {OrderDetail} orderDetail - Detalle de la orden
   * @param {PrintContext} context - Contexto de impresión (order, kitchen, self_service, auto_print)
   * @returns {Promise<boolean>} True si se envió correctamente
   */
  const sendToPrintServer = async (orderDetail: OrderDetail, context: PrintContext): Promise<boolean> => {
    const { config } = getPlickPrintServerConfig();

    if (!isPrintServerConfigured()) {
      return false;
    }

    try {
      setIsLoading(true); // Kept this line

      const printers = getPrintersForContext(context);
      if (printers.length === 0) return false;

      // Obtener credenciales globales
      const globalUrl = config?.url;
      const globalApiKey = config?.api_key;

      if (!globalUrl || !globalApiKey) {
        toast.error('Faltan credenciales del servidor de impresión');
        return false;
      }

      // Determinar el payload según el contexto
      // Si es cocina -> Ticket Simplificado
      // Si es otro (order, self_service, auto_print genérico) -> Ticket Normal
      // Se pasa 'commerce' como segundo argumento para el pie de página
      const payload =
        context === 'kitchen'
          ? buildKitchenTicketPayload(orderDetail, commerce || undefined)
          : buildTicketPayload(orderDetail, commerce || undefined);

      const promises = printers.map(async (printer) => {
        const jobPayload: PrintJobPayload = {
          queueId: `plick-${Date.now()}`,
          jobId: `job-${orderDetail.pedido.id}-${Date.now()}`,
          createdAt: new Date().toISOString(),
          priority: 'normal',
          printerId: printer.printer_id,
          payloadType: 'raw',
          payload: payload,
          metadata: {
            copies: 1,
            cut: true,
            paperWidth: 80,
          },
        };

        const response = await fetch(`${globalUrl}/print`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${globalApiKey}`,
          },
          body: JSON.stringify(jobPayload),
        });

        if (!response.ok) {
          throw new Error(`${printer.name}: HTTP ${response.status}`);
        }

        return response.json();
      });

      await Promise.all(promises);
      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error desconocido';
      toast.error(`Error al enviar al servidor de impresión: ${errorMessage}`);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Prueba la conexión con el servidor de impresión
   */
  const testPrinterConnection = async (): Promise<boolean> => {
    const { config } = getPlickPrintServerConfig();
    const globalUrl = config?.url;

    if (!globalUrl) {
      toast.error('No hay URL configurada');
      return false;
    }

    try {
      const response = await fetch(`${globalUrl}/health`);
      if (response.ok) {
        toast.success('✅ Conexión exitosa con el servidor');
        return true;
      }
      toast.error('❌ El servidor no responde correctamente');
      return false;
    } catch (error) {
      toast.error('❌ No se pudo conectar al servidor');
      return false;
    }
  };

  /**
   * Envía un ticket de prueba a una impresora específica
   * @param {string} printerId - ID de la impresora
   * @returns {Promise<boolean>} True si se envió correctamente
   */
  const testPrint = async (printerId: string): Promise<boolean> => {
    const { config } = getPlickPrintServerConfig();
    const globalUrl = config?.url;
    const globalApiKey = config?.api_key;

    if (!globalUrl || !globalApiKey) {
      toast.error('Faltan credenciales del servidor');
      return false;
    }

    try {
      const testPayload: PrintJobPayload = {
        queueId: 'default',
        jobId: `test-${Date.now()}`,
        createdAt: new Date().toISOString(),
        priority: 'normal',
        printerId: printerId,
        payloadType: 'raw',
        payload: [
          '\x1B\x61\x01', // Centrar
          '===== TEST PLICK =====\n',
          '\x1B\x61\x00', // Izquierda
          `Fecha: ${new Date().toLocaleString('es-MX')}\n`,
          '\n',
          'Impresora funcionando correctamente\n',
          '\n\n',
          '\x1D\x56\x00', // Cortar
        ].join(''),
        metadata: {
          copies: 1,
          cut: true,
          paperWidth: 80,
        },
      };

      const response = await fetch(`${globalUrl}/print`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${globalApiKey}`,
        },
        body: JSON.stringify(testPayload),
      });

      if (response.ok) {
        toast.success('✅ Ticket de prueba enviado');
        return true;
      }
      toast.error('❌ Error al imprimir');
      return false;
    } catch (error) {
      toast.error('❌ No se pudo enviar la impresión');
      return false;
    }
  };

  return {
    sendToPrintServer,
    isPrintServerConfigured,
    getPrintersForContext,
    testPrinterConnection,
    testPrint,
    isLoading,
  };
};
